import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  writeBatch,
} from "firebase/firestore";
import {
  parseScannedTime,
  partitionScans,
  todayKey,
} from "./stocktakePurgeHelper";

const COLLECTION = "stocktake_scanned";
const BATCH_SIZE = 500; // Firestore max writes per batch

// Selective sibling of ClearStocktakeScanned: instead of wiping the whole
// collection, this deletes only scans dated BEFORE a chosen cutoff day
// (default = today), so today's fresh scans are left untouched.
const ClearOldStocktakeScanned = () => {
  const db = getFirestore();

  const [scans, setScans] = useState([]); // [{ id, time: Date|null }]
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [cutoffKey, setCutoffKey] = useState(() => todayKey());
  const [deleteUndated, setDeleteUndated] = useState(false);

  const load = async () => {
    setLoading(true);
    setStatus("Loading scans…");
    try {
      const snap = await getDocs(collection(db, COLLECTION));
      const list = snap.docs.map((d) => ({
        id: d.id,
        time: parseScannedTime(d.data() || {}),
      }));
      setScans(list);
      setStatus(`Loaded ${list.length} scans.`);
    } catch (e) {
      console.error(e);
      setStatus("Failed to load scans. Check console.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo(
    () => partitionScans(scans, { cutoffKey, deleteUndated }),
    [scans, cutoffKey, deleteUndated]
  );

  const handleDelete = async () => {
    const { toDelete, deleteCount, keepCount } = summary;

    if (!cutoffKey) {
      setStatus("Pick a cutoff date first.");
      return;
    }
    if (deleteCount === 0) {
      setStatus("Nothing to delete — no scans are older than the cutoff.");
      return;
    }

    const ok = window.confirm(
      `⚠️ Permanently delete ${deleteCount} scan(s) dated BEFORE ${cutoffKey}?\n\n` +
        `${keepCount} scan(s) on/after ${cutoffKey} will be KEPT.\n` +
        (deleteUndated
          ? "Scans with no date WILL also be deleted."
          : "Scans with no date will be kept.") +
        "\n\nThis cannot be undone."
    );
    if (!ok) return;

    setBusy(true);
    setStatus(`Deleting 0 / ${deleteCount}…`);
    try {
      let done = 0;
      for (let i = 0; i < toDelete.length; i += BATCH_SIZE) {
        const chunk = toDelete.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        for (const id of chunk) batch.delete(doc(db, COLLECTION, id));
        await batch.commit();
        done += chunk.length;
        setStatus(`Deleting ${done} / ${deleteCount}…`);
      }
      setStatus(`Deleted ${done} old scan(s). Reloading…`);
      await load();
    } catch (e) {
      console.error(e);
      setStatus("Error while deleting — some scans may remain. Check console.");
    } finally {
      setBusy(false);
    }
  };

  const disabled = loading || busy;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-1">🧹 Clear Old Stocktake Scans</h1>
      <p className="text-sm text-gray-600 mb-4">
        Deletes scans from <code>{COLLECTION}</code> that were scanned{" "}
        <strong>before</strong> the cutoff date. Scans on or after the cutoff are
        kept — set the cutoff to today to keep today&apos;s stocktake.
      </p>

      <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-4">
        <label className="flex flex-col text-sm">
          <span className="mb-1 font-medium text-gray-700">
            Keep scans on/after
          </span>
          <input
            type="date"
            value={cutoffKey}
            onChange={(e) => setCutoffKey(e.target.value)}
            disabled={disabled}
            className="p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          />
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={deleteUndated}
            onChange={(e) => setDeleteUndated(e.target.checked)}
            disabled={disabled}
          />
          Also delete scans with no date ({summary.undatedCount})
        </label>

        <button
          onClick={load}
          disabled={disabled}
          className="px-4 py-2 rounded-md border text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label="Total scans" value={scans.length} tone="gray" />
        <Stat label="Will delete" value={summary.deleteCount} tone="red" />
        <Stat label="Will keep" value={summary.keepCount} tone="green" />
      </div>

      <button
        onClick={handleDelete}
        disabled={disabled || summary.deleteCount === 0}
        className={`w-full py-3 rounded-md text-white font-medium ${
          disabled || summary.deleteCount === 0
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-red-600 hover:bg-red-700"
        }`}
      >
        {busy
          ? "Working…"
          : `Delete ${summary.deleteCount} old scan(s) before ${cutoffKey || "…"}`}
      </button>

      {status && <p className="mt-3 text-sm text-gray-700">{status}</p>}

      {/* Per-date breakdown so you can see exactly what goes and what stays */}
      {!loading && summary.byDate.length > 0 && (
        <div className="mt-6 overflow-x-auto border rounded">
          <table className="min-w-full bg-white text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-right">Scans</th>
                <th className="px-3 py-2 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {summary.byDate.map((row) => (
                <tr
                  key={row.dateKey}
                  className={`border-t ${row.willDelete ? "bg-red-50" : "bg-green-50"}`}
                >
                  <td className="px-3 py-2">{row.dateKey}</td>
                  <td className="px-3 py-2 text-right">{row.count}</td>
                  <td className="px-3 py-2">
                    {row.willDelete ? (
                      <span className="text-red-700 font-medium">Delete</span>
                    ) : (
                      <span className="text-green-700 font-medium">Keep</span>
                    )}
                  </td>
                </tr>
              ))}
              {summary.undatedCount > 0 && (
                <tr className={`border-t ${deleteUndated ? "bg-red-50" : "bg-gray-50"}`}>
                  <td className="px-3 py-2 italic text-gray-600">No date</td>
                  <td className="px-3 py-2 text-right">{summary.undatedCount}</td>
                  <td className="px-3 py-2">
                    {deleteUndated ? (
                      <span className="text-red-700 font-medium">Delete</span>
                    ) : (
                      <span className="text-gray-600 font-medium">Keep</span>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const toneClasses = {
  gray: "bg-gray-50 text-gray-800",
  red: "bg-red-50 text-red-700",
  green: "bg-green-50 text-green-700",
};

const Stat = ({ label, value, tone }) => (
  <div className={`rounded-md p-3 text-center ${toneClasses[tone] || toneClasses.gray}`}>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs uppercase tracking-wide">{label}</div>
  </div>
);

export default ClearOldStocktakeScanned;
